<script setup lang="tsx">
import { onDeactivated, onUnmounted, ref } from "vue";
import { searchToRecord } from "@zyzgroup/core-common";
import { mapState, context } from "../../context/context";
import { dialog, loading, popTel } from "../../globalTeleport";
import { pageShow$, routeChange$, useRouter } from "../../hooks";
import Page from "../base/Page.vue";
import Footer from "./Footer.vue";
import PlateDisplay from "../plate/PlateDisplay.vue";
import ImageView from "../image/ImageView.vue";
import ContentCenter from "../layout/ContentCenter.vue";
import IconArrowRight from "../icon/IconArrowRight.vue";

// for merge ce shadow-root styles
defineOptions({
  customOptions: {
    Page,
    Footer,
    PlateDisplay,
    ImageView,
    ContentCenter,
    IconArrowRight
  },
  inheritAttrs: true
});

const props = withDefaults(
  defineProps<{
    lottag: string;
  }>(),
  {}
);

const mappedState = mapState([
  "bill.sn",
  "bill.feeFormatted",
  "park.tag",
  "park.name",
  "park.phone",
  "park.feebookurl",
  "lot.no",
  "order.sn",
  "order.fee",
  "order.feeFormatted",
  "order.starttimeFormatted",
  "order.duringtimeFormatted",
  "order.pics",
  "plate.color",
  "plate.plate",
  "user.openid",
  "color.primaryPattern",
  "color.gradientPattern",
  "color.fontSecondaryPattern",
  "color.foregroundPattern",
  "color.foregroundBorderPattern",
  "color.fontPattern",
  "color.redCommon",
  "color.yellowCommon"
]);

const desc = ref<string[]>([]);

function loadOrder(lottag) {
  context.ds.immediately(loading);
  context.axios
    // .post(`/api/v6/lot/${lottag}/getwithorder6`, {
    .post(`/api/v6/lot/${lottag}/genbill`, {
      openid: mappedState["user.openid"].value,
      paytype: "U"
    })
    .catch((result) => {
      // context.ds.closeLast()
      // context.store.dispatch({
      //   type: "SET_RESULT",
      //   payload: {
      //     status: "error",
      //     title: result.data.error || JSON.stringify(result.data)
      //   }
      // });
      context.ds.immediately(dialog, {
        title: "接口出错",
        content: result.data.error,
        confirmText: "知道了",
        mode: "mini",
        onClose: () => {}
      });
      return result;
    })
    .then((result) => {
      context.ds.closeLast();
      context.store.dispatch({
        type: "SET_PARK",
        payload: result.data.pk || {}
      });
      context.store.dispatch({
        type: "SET_LOT",
        payload: result.data.pl || {}
      });
      context.store.dispatch({
        type: "SET_BILL",
        payload: result.data.bill || {}
      });
      context.store.dispatch({
        type: "SET_ORDER",
        payload: {
          ...result.data.uo,
          pics: result.data.pics
        }
      });
      context.store.dispatch({
        type: "SET_PLATE",
        payload: result.data.uo || {}
      });
      context.store.dispatch({
        type: "SET_GROUP",
        payload: result.data.grp || {}
      });
      desc.value = result.data.sdesc || [];
    });
}

const router = useRouter();

const token = ref("");

const rs$ = pageShow$("lot").subscribe(() => {
  // console.log("** show **");
  // 监听到当前页面显示
  // 对应二维码 https://tstxg.ruolin-link.com/l?n=2175
  // 本地链接   http://192.168.1.30/l.html?n=2175  http://127.0.0.1/l.html?n=739#/
  // 测试服链接 https://test.xinge.me/l.html?n=2175
  // 正式服链接 https://xinge.ruolin-link.com/xg/l?n=2175
  const records = searchToRecord();
  const lottag = records.n || props.lottag;
  if (!lottag) {
    context.ds.immediately(dialog, {
      title: "扫码失败",
      content: `lottag=${lottag} 不符规则，请重扫或联系客服：400-158-8363`,
      confirmText: "知道了",
      mode: "mini"
    });
  } else {
    loadOrder(lottag);
  }
  const tokenLocal = context.static.token.get();
  if (tokenLocal) {
    token.value = tokenLocal;
    context.store.dispatch({
      type: "SET_USER",
      payload: { token: tokenLocal }
    });
  }
});

function destroy() {
  rs$.unsubscribe();
}
onDeactivated(() => {
  destroy();
});

function viewDocument() {
  const url = mappedState["park.feebookurl"].value;
  if (!url) {
    return;
  }
  router.push({
    name: "view-document",
    params: {
      title: "收费标准",
      src: encodeURIComponent(url)
    }
  });
}

const imageViewEl = ref();
function viewPhotos() {
  const pics = mappedState["order.pics"].value;
  if (!pics.length) {
    return;
  }
  imageViewEl.value?.open();
}
</script>

<template>
  <Page
    v-if="mappedState['lot.no'].value"
    class="lot"
  >
    <div
      class="lot-main-bg"
      :style="{
        background: `linear-gradient(180deg, ${mappedState['color.primaryPattern'].value} 0%, ${mappedState['color.gradientPattern'].value} 100%)`
      }"
    ></div>
    <div
      class="lot-main"
      :style="{ color: `${mappedState['color.foregroundPattern'].value}` }"
    >
      <div>{{ mappedState["park.name"].value }}</div>
      <div>
        <div
          :style="{
            background: `linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, ${mappedState['color.foregroundPattern'].value} 100%)`
          }"
        ></div>
        <div>编号</div>
        <div
          :style="{
            background: `linear-gradient(90deg,  ${mappedState['color.foregroundPattern'].value} 0%, rgba(255, 255, 255, 0) 100%)`
          }"
        ></div>
      </div>
      <div>
        <span>{{ mappedState["park.tag"].value }}</span>
        <span>{{ mappedState["lot.no"].value }}</span>
      </div>
      <div>
        <!-- <div class="disabled">停车场主页</div>
        <div class="disabled">更换附近车位</div> -->
        <div
          :class="mappedState['park.feebookurl'].value ? '' : 'disabled'"
          @click="viewDocument"
        >
          收费标准
        </div>
      </div>
      <div
        v-if="!mappedState['bill.sn'].value"
        class="error-info"
        :style="{
          background: `${mappedState['color.foregroundPattern'].value}`,
          color: `${mappedState['color.fontPattern'].value}`
        }"
      >
        <div v-for="item in desc">{{ item }}</div>
      </div>
      <div
        v-else
        class="lot-detail"
        :style="{
          background: `${mappedState['color.foregroundPattern'].value}`,
          color: `${mappedState['color.fontPattern'].value}`
        }"
      >
        <div
          :style="{
            borderBottom: `1px solid ${mappedState['color.foregroundBorderPattern'].value}`
          }"
        >
          <div
            :style="{
              color: `${mappedState['color.fontSecondaryPattern'].value}`
            }"
          >
            待支付
          </div>
          <div
            :style="{
              color: `${mappedState['color.redCommon'].value}`
            }"
          >
            ￥{{ mappedState["bill.feeFormatted"].value }}
          </div>
        </div>
        <div v-if="mappedState['order.fee'].value">
          <div
            :style="{
              color: `${mappedState['color.fontSecondaryPattern'].value}`
            }"
          >
            已支付
          </div>
          <div>￥{{ mappedState["order.feeFormatted"].value }}</div>
        </div>
        <div>
          <div
            :style="{
              color: `${mappedState['color.fontSecondaryPattern'].value}`
            }"
          >
            订单编号
          </div>
          <div>{{ mappedState["order.sn"].value || "-" }}</div>
        </div>
        <div>
          <div
            :style="{
              color: `${mappedState['color.fontSecondaryPattern'].value}`
            }"
          >
            开始时间
          </div>
          <div>{{ mappedState["order.starttimeFormatted"].value || "-" }}</div>
        </div>
        <div>
          <div
            :style="{
              color: `${mappedState['color.fontSecondaryPattern'].value}`
            }"
          >
            停车时长
          </div>
          <div>{{ mappedState["order.duringtimeFormatted"].value || "-" }}</div>
        </div>
        <!-- <div>
          <div>优惠券</div>
          <div class="red">1分钟</div>
        </div>
        <div>
          <div>优惠信息</div>
          <div class="red">xxxx</div>
        </div> -->
        <!-- <div>
          <div>包月开始时间</div>
          <div>2025-04-08 09:41:25</div>
        </div>
        <div>
          <div>包月结束时间</div>
          <div>2025-04-08 09:41:25</div>
        </div> -->
        <div
          v-if="
            mappedState['plate.plate'].value ||
            mappedState['order.pics'].value.length
          "
          :style="{
            borderTop: `1px solid ${mappedState['color.foregroundBorderPattern'].value}`
          }"
        ></div>
        <div v-if="mappedState['plate.plate'].value">
          <div
            :style="{
              color: `${mappedState['color.fontSecondaryPattern'].value}`
            }"
          >
            车牌号
          </div>
          <div class="bold">
            <PlateDisplay
              :plate="mappedState['plate.plate'].value"
              :plate-color="mappedState['plate.color'].value"
              font-weight="bold"
            ></PlateDisplay>
          </div>
        </div>
        <div v-if="mappedState['order.pics'].value.length">
          <div
            :style="{
              color: `${mappedState['color.fontSecondaryPattern'].value}`
            }"
          >
            现场照片
          </div>
          <div
            class="blue"
            @click="viewPhotos"
          >
            查看
          </div>
        </div>
      </div>
      <div
        class="lot-actions"
        :style="{
          background: `${mappedState['color.foregroundPattern'].value}`,
          color: `${mappedState['color.primaryPattern'].value}`
        }"
      >
        <ContentCenter
          v-if="!token"
          direction="row"
          class="lot-action-login"
          :style="{
            background: `${mappedState['color.yellowCommon'].value}`,
            color: `${mappedState['color.foregroundPattern'].value}`
          }"
          @click="() => router.push('/login')"
        >
          <div>立即登录</div>
          <div style="width: 14px">
            <IconArrowRight
              :color="mappedState['color.foregroundPattern'].value"
            ></IconArrowRight>
          </div>
        </ContentCenter>
        <div
          class="lot-action"
          @click="
            () =>
              popTel(mappedState['park.phone'].value || context.static.hotline)
          "
        >
          联系客服
        </div>
      </div>
    </div>
    <template #footer>
      <Footer
        v-if="mappedState['bill.sn'].value"
        :billsn="mappedState['bill.sn'].value"
        @reloadOrder="() => loadOrder(lottag)"
      ></Footer>
    </template>
    <template #global>
      <ImageView
        ref="imageViewEl"
        :urls="mappedState['order.pics'].value"
      ></ImageView>
    </template>
  </Page>
</template>

<style scoped>
.lot-main-bg {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  width: 100%;
  height: 5.2rem;
  border-bottom-left-radius: 2rem 0.5rem;
  border-bottom-right-radius: 2rem 0.5rem;
}
.lot-main {
  position: relative;
  z-index: 2;
  width: 100%;
  overflow: hidden;
  box-sizing: border-box;
  line-height: 1;
  padding: 0 var(--yz-padding-horizontal);
  margin-bottom: var(--yz-padding-horizontal);
  text-align: center;
}
.lot-main > div:nth-child(1) {
  font-size: 0.32rem;
  padding-top: 0.4rem;
  word-break: break-all;
  font-weight: bold;
}
.lot-main > div:nth-child(2) > div:nth-child(1) {
  width: 2.4rem;
  height: 0.04rem;
}
.lot-main > div:nth-child(2) > div:nth-child(3) {
  width: 2.4rem;
  height: 0.04rem;
}
.lot-main > div:nth-child(2) > div:nth-child(2) {
  width: 1rem;
  font-size: 0.28rem;
  line-height: 0.32rem;
}
.lot-main > div:nth-child(2) {
  display: flex;
  align-items: center;
  margin: 0.28rem auto 0;
  width: 4.8rem;
  position: relative;
}
.lot-main > div:nth-child(3) {
  font-family: "D-DINExp-number";
  font-weight: bold;
  margin-top: 0.12rem;
  font-size: 0.8rem;
  letter-spacing: 0.06rem;
}
.lot-main > div:nth-child(3) > span + span {
  margin-left: 0.3rem;
}
.lot-main > div:nth-child(4) {
  display: flex;
  justify-content: space-between;
  font-size: 0.28rem;
  line-height: 0.28rem;
  margin: 0.3rem 0;
}
.lot-main > div:nth-child(4) > div {
  flex: 1 1 auto;
  text-align: center;
  cursor: pointer;
}
.lot-main > div:nth-child(4) > div.disabled {
  opacity: var(--yz-disabled-alpha);
}
.lot-main > div:nth-child(4) > div + div {
  border-left: 1px solid rgba(255, 255, 255, var(--yz-disabled-alpha));
}
.error-info {
  min-height: 3.8rem;
  padding: 0.4rem 0.3rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  box-shadow: 0 0.04rem 0.1rem rgba(0, 0, 0, 0.1);
  border-radius: 0.08rem;
  font-size: 14px;
  line-height: 19px;
  word-break: break-all;
  text-align: center;
}
.error-info > div:nth-child(1) {
  font-size: 16px;
  line-height: 24px;
  font-weight: bold;
}
.error-info > div + div {
  margin-top: 0.14rem;
}
.lot-detail {
  position: relative;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  padding: 0 var(--yz-padding-horizontal) 5px;
  font-size: 14px;
  line-height: 20px;
}
.lot-detail > div {
  margin: var(--yz-padding-vertical) auto;
  display: flex;
  justify-content: space-between;
}
.lot-detail > div:nth-child(1) {
  line-height: 44px;
  margin: 0 0 var(--yz-padding-vertical);
  font-weight: bold;
}
.lot-detail > div:nth-child(1) > div:nth-child(2) {
  font-size: 20px;
}
.lot-detail > div > div:nth-child(1) {
  flex: 0 0 auto;
  padding-right: 10px;
}
.lot-detail > div > div:nth-child(2).blue {
  color: var(--yz-xg-blue);
  cursor: pointer;
}
.lot-actions {
  margin: 10px 0;
  box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  display: flex;
  align-items: center;
  font-size: 14px;
  position: relative;
}
.lot-actions > .lot-action {
  flex: 1;
  line-height: 44px;
  position: relative;
  cursor: pointer;
  text-align: center;
  font-weight: 700;
}
.lot-actions > .lot-action-login {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translate(0, -50%);
  padding: 8px 8px;
  border-radius: 0 100px 100px 0;
  z-index: 2;
  cursor: pointer;
}
</style>
